// Builders for the live event payloads. Field names and types match the
// REFSIX models so they validate against the same shapes the Apple Watch posts.

import {
  HOME_TEAM,
  AWAY_TEAM,
  SINGLE_HALF_LENGTH_MS,
  findPlayer,
  DEFAULT_GOAL_SCORER_NUMBER,
  DEFAULT_CARDED_PLAYER_NUMBER,
  DEFAULT_SUB_OFF_NUMBER,
  DEFAULT_SUB_ON_NUMBER,
} from "./defaults";

export type Side = "home" | "away";
export type HalfNumber = 1 | 2;

export const HALF_TIME_LENGTH_MS = 15 * 60 * 1000;

// Half length in minutes — matches `period1`/`period2` in MatchPhone.timings.
const HALF_LENGTH_MIN = SINGLE_HALF_LENGTH_MS / 60_000;

// minuteOfPlay is cumulative from match start (watch convention): a goal in
// the 11th minute of the 2nd half POSTs minuteOfPlay = 56, not 11.
function cumulativeMinute(
  now: number,
  currentHalfKickOff: number,
  half: HalfNumber
): number {
  const inHalf = Math.max(0, Math.floor((now - currentHalfKickOff) / 60000));
  return (half - 1) * HALF_LENGTH_MIN + inHalf;
}

// 1st half. The watch sets kickOffSide for which team kicks off; we hardcode
// 0 (home) here and flip to 1 (away) for the 2nd half. The presence of
// `endTime` is what distinguishes a finished half from an in-progress one;
// see refauth LiveMatchEventsController.js which keys events by timestamp.
export function buildKickOffEvent(kickOffTime: number) {
  return {
    eventName: "Half",
    timestamp: kickOffTime,
    half: 1,
    minuteOfPlay: 0,
    index: 0,
    name: "First Half",
    playing: true,
    length: SINGLE_HALF_LENGTH_MS,
    kickOffSide: 0,
    injuryTimeAccumulated: 0,
    injuryTimeInInjuryTimeAccumulated: 0,
    isExtraTime: false,
  } as const;
}

export function buildHalfEndEvent(kickOffTime: number, endTime: number) {
  const minutes = Math.max(1, Math.floor((endTime - kickOffTime) / 60000));
  return {
    ...buildKickOffEvent(kickOffTime),
    endTime,
    endMinute: minutes,
  };
}

// Half-time interval. We POST it with both timestamp and endTime set because
// the tester skips through half time on a single click — the watch normally
// posts it twice (start, then re-post with endTime).
export function buildHalfTimeEvent(startTime: number, endTime: number) {
  const minutes = Math.max(1, Math.floor((endTime - startTime) / 60000));
  return {
    eventName: "Half",
    timestamp: startTime,
    half: 1, // watch keeps half time in first-half context
    minuteOfPlay: HALF_LENGTH_MIN,
    index: 1,
    name: "Half Time",
    playing: false,
    length: HALF_TIME_LENGTH_MS,
    injuryTimeAccumulated: 0,
    injuryTimeInInjuryTimeAccumulated: 0,
    isExtraTime: false,
    endTime,
    endMinute: minutes,
  } as const;
}

export function buildSecondHalfKickOffEvent(kickOffTime: number) {
  return {
    eventName: "Half",
    timestamp: kickOffTime,
    half: 2,
    minuteOfPlay: HALF_LENGTH_MIN,
    index: 2,
    name: "Second Half",
    playing: true,
    length: SINGLE_HALF_LENGTH_MS,
    kickOffSide: 1,
    injuryTimeAccumulated: 0,
    injuryTimeInInjuryTimeAccumulated: 0,
    isExtraTime: false,
  } as const;
}

export function buildSecondHalfEndEvent(kickOffTime: number, endTime: number) {
  const inHalf = Math.max(1, Math.floor((endTime - kickOffTime) / 60000));
  return {
    ...buildSecondHalfKickOffEvent(kickOffTime),
    endTime,
    endMinute: HALF_LENGTH_MIN + inHalf,
  };
}

export function buildGoalEvent(
  currentHalfKickOff: number,
  side: Side,
  now: number,
  half: HalfNumber = 1,
  playerNumber: number = DEFAULT_GOAL_SCORER_NUMBER
) {
  return {
    eventName: "Goal",
    timestamp: now,
    half,
    minuteOfPlay: cumulativeMinute(now, currentHalfKickOff, half),
    team: side === "home" ? HOME_TEAM : AWAY_TEAM,
    player: findPlayer(side, playerNumber),
    ownGoal: false,
    penalty: false,
    freeKick: false,
  } as const;
}

export function buildIncidentEvent(
  currentHalfKickOff: number,
  side: Side,
  now: number,
  card: "yellow" | "red",
  half: HalfNumber = 1,
  playerNumber: number = DEFAULT_CARDED_PLAYER_NUMBER,
  reason?: string
) {
  return {
    eventName: "Incident",
    timestamp: now,
    half,
    minuteOfPlay: cumulativeMinute(now, currentHalfKickOff, half),
    team: side === "home" ? HOME_TEAM : AWAY_TEAM,
    player: findPlayer(side, playerNumber),
    reason:
      reason ??
      (card === "yellow" ? "Unsporting behaviour" : "Serious foul play"),
    card,
    duringSinBin: false,
    sinBin: false,
  } as const;
}

export function buildSubstitutionEvent(
  currentHalfKickOff: number,
  side: Side,
  now: number,
  half: HalfNumber = 1,
  playerOffNumber: number = DEFAULT_SUB_OFF_NUMBER,
  playerOnNumber: number = DEFAULT_SUB_ON_NUMBER
) {
  return {
    eventName: "Substitution",
    timestamp: now,
    half,
    minuteOfPlay: cumulativeMinute(now, currentHalfKickOff, half),
    team: side === "home" ? HOME_TEAM : AWAY_TEAM,
    playerOff: findPlayer(side, playerOffNumber),
    playerOn: findPlayer(side, playerOnNumber),
  } as const;
}
